import { defineMessages, useIntl } from 'react-intl-next';

import type { PaginatedSelectedFilterProps } from '@atlassian/filter-controller';
import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { getTagDisplayName, TAG_IDS } from '../constants';
import type { Tag } from '../types';

export const messages = defineMessages({
  contextualTags: {
    id: 'appUpdates.filters.tags.label',
    description: 'Label for the AI-generated contextual tags filter',
    defaultMessage: 'Contextual tags',
  },
});

// Defined outside component to maintain stable references
const tagOptions: Tag[] = TAG_IDS.map((tagId) => ({
  id: tagId,
  name: getTagDisplayName(tagId),
}));

const tagSelectOptions: SingleOption[] = tagOptions.map(
  (tag): SingleOption => ({
    label: tag.name,
    value: tag.id,
    optionType: 'option',
  }),
);

export const getTagsFromOptions = (
  options: readonly SingleOption[],
): Tag[] | null => {
  const selectedTags = options
    .map((option) =>
      tagOptions.find((candidate) => candidate.id === option.value),
    )
    .filter((tag): tag is Tag => tag !== undefined);

  return selectedTags.length > 0 ? selectedTags : null;
};

export const useTagFilterConfig = (): PaginatedSelectedFilterProps => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { sendUIEvent } = useAnalyticsEvent();

  // Derive selected values by looking up from stable options list
  const getSelectedTags = (): SingleOption[] => {
    const selectedTags = state.updates.draftFilters.selectedTags;
    if (!selectedTags || selectedTags.length === 0) {
      return [];
    }
    return selectedTags
      .map((selected) =>
        tagSelectOptions.find((option) => option.value === selected.id),
      )
      .filter((option): option is SingleOption => option !== undefined);
  };

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = tagSelectOptions.map((option) => ({
      label: option.label,
      value: option.value,
      optionType: 'option',
    }));

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({
      options,
      totalCount: options.length,
    });
  };

  const handleChange = (options: readonly SingleOption[]) => {
    const nextSelectedTags = getTagsFromOptions(options) ?? [];

    dispatch({
      type: 'FILTER_TAG_SELECT',
      payload: {
        selectedTags: nextSelectedTags.length > 0 ? nextSelectedTags : null,
      },
    });

    // Send analytics event
    const nextSelectedTagsString =
      nextSelectedTags.length > 0
        ? nextSelectedTags.map((t) => t.id).join(',')
        : undefined;
    sendUIEvent(
      appUpdatesUIEvents.ui.appUpdatesTagFilterClicked(
        { source: 'appUpdatesListScreen' },
        nextSelectedTagsString,
      ).data,
    );
  };

  return {
    testId: 'app-updates-filter-tag-testid',
    fieldLabel: formatMessage(messages.contextualTags),
    fieldId: 'app-updates-table-tag-filter',
    value: getSelectedTags(),
    onLoadOptions,
    onChange: handleChange,
    onClearValues: () => handleChange([]),
  };
};
